import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Avatar,
  Chip,
  Button,
  alpha,
  Divider,
  Tabs,
  Tab,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  X,
  Bell,
  Check,
  CheckCheck,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  Trash2,
  Settings,
  RefreshCw,
  Package,
  FileText,
  Briefcase,
  Users,
  Calendar,
  Clock,
  DollarSign,
} from 'lucide-react';

const mockNotifications = [
  {
    id: 1,
    type: 'alert',
    title: 'Permit Expiring Soon',
    message: 'J-2024-003 Airport Terminal permit expires in 3 days',
    time: '5 minutes ago',
    read: false,
    priority: 'high',
    category: 'permits',
  },
  {
    id: 2,
    type: 'success',
    title: 'Job Completed',
    message: 'J-2024-001 City Center Mall installation completed successfully',
    time: '1 hour ago',
    read: false,
    priority: 'normal',
    category: 'jobs',
  },
  {
    id: 3,
    type: 'info',
    title: 'New Estimate Request',
    message: 'Tech Corp submitted a new estimate request for lobby signage',
    time: '2 hours ago',
    read: false,
    priority: 'normal',
    category: 'estimates',
  },
  {
    id: 4,
    type: 'warning',
    title: 'Low Inventory Alert',
    message: 'LED Channel Letters stock is below minimum threshold (15 units)',
    time: '3 hours ago',
    read: true,
    priority: 'high',
    category: 'inventory',
  },
  {
    id: 5,
    type: 'info',
    title: 'Schedule Update',
    message: 'Installation for J-2024-002 rescheduled to next Monday',
    time: '5 hours ago',
    read: true,
    priority: 'normal',
    category: 'schedule',
  },
  {
    id: 6,
    type: 'success',
    title: 'Payment Received',
    message: 'Westfield Corp payment of $45,000 has been received',
    time: '1 day ago',
    read: true,
    priority: 'normal',
    category: 'payments',
  },
  {
    id: 7,
    type: 'alert',
    title: 'Approval Required',
    message: 'Design review for University Campus needs your approval',
    time: '1 day ago',
    read: true,
    priority: 'high',
    category: 'approvals',
  },
];

const notificationTypeConfig = {
  alert: { icon: AlertTriangle, color: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)' },
  warning: { icon: AlertTriangle, color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
  info: { icon: Info, color: '#6366F1', bg: 'rgba(99, 102, 241, 0.1)' },
  success: { icon: CheckCircle, color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)' },
  error: { icon: XCircle, color: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)' },
};

const categoryIconConfig = {
  jobs: Briefcase,
  permits: FileText,
  estimates: DollarSign,
  inventory: Package,
  schedule: Calendar,
  payments: DollarSign,
  approvals: Check,
};

function NotificationCenter({ open, onClose, onNavigate }) {
  const [notifications, setNotifications] = useState(mockNotifications);
  const [activeTab, setActiveTab] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const highPriorityCount = notifications.filter((n) => n.priority === 'high' && !n.read).length;

  const filteredNotifications = notifications.filter((n) => {
    if (activeTab === 0) return true; // All
    if (activeTab === 1) return !n.read; // Unread
    if (activeTab === 2) return n.priority === 'high'; // Priority
    return true;
  });

  const handleMarkRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleDelete = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleClearAll = () => {
    setNotifications([]);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsRefreshing(false);
  };

  const handleNotificationClick = (notification) => {
    handleMarkRead(notification.id);
    // Navigate to relevant page based on category
    if (onNavigate && notification.category) {
      onNavigate(notification.category);
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          borderRadius: '24px 0 0 24px',
          boxShadow: '-8px 0 30px -10px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Badge badgeContent={unreadCount} color="error">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px -8px rgba(99, 102, 241, 0.4)',
                }}
              >
                <Bell size={24} color="#ffffff" />
              </Box>
            </Badge>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {unreadCount} unread • {highPriorityCount} priority
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <X size={20} />
          </IconButton>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<CheckCheck size={16} />}
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Mark all read
          </Button>
          <Button
            size="small"
            startIcon={<Trash2 size={16} />}
            onClick={handleClearAll}
            disabled={notifications.length === 0}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            Clear all
          </Button>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh notifications">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw
                size={18}
                style={{
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notification settings">
            <IconButton size="small">
              <Settings size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 'auto',
              px: 2,
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                All
                <Chip
                  label={notifications.length}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Unread
                {unreadCount > 0 && (
                  <Chip
                    label={unreadCount}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Priority
                {highPriorityCount > 0 && (
                  <Chip
                    label={highPriorityCount}
                    size="small"
                    color="error"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Notification List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredNotifications.length > 0 ? (
          <List sx={{ p: 1 }}>
            {filteredNotifications.map((notification) => {
              const typeConfig = notificationTypeConfig[notification.type];
              const TypeIcon = typeConfig.icon;
              const CategoryIcon = categoryIconConfig[notification.category] || Info;

              return (
                <ListItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    mb: 1,
                    p: 2,
                    borderRadius: 3,
                    cursor: 'pointer',
                    bgcolor: notification.read
                      ? 'transparent'
                      : alpha('#6366F1', 0.04),
                    border: '1px solid',
                    borderColor: notification.read
                      ? 'divider'
                      : alpha('#6366F1', 0.2),
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha('#6366F1', 0.06),
                      borderColor: alpha('#6366F1', 0.3),
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    {/* Icon */}
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: typeConfig.bg,
                        flexShrink: 0,
                      }}
                    >
                      <TypeIcon size={20} color={typeConfig.color} />
                    </Avatar>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={notification.read ? 500 : 700}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.read && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              flexShrink: 0,
                              mt: 0.75,
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {notification.message}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 1.5,
                        }}
                      >
                        <Chip
                          icon={<CategoryIcon size={12} />}
                          label={notification.category}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        />
                        {notification.priority === 'high' && (
                          <Chip
                            label="Priority"
                            size="small"
                            color="error"
                            sx={{
                              height: 22,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <Clock size={12} />
                          {notification.time}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
            }}
          >
            <Bell size={48} color="#CBD5E1" />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 2, fontWeight: 500 }}
            >
              No notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeTab === 1
                ? "You're all caught up!"
                : activeTab === 2
                ? 'No priority notifications'
                : 'Check back later for updates'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          View All Notifications
        </Button>
      </Box>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Drawer>
  );
}

export default NotificationCenter;
